import { BenefitEnrollmentFlowContainer } from "#/components/cmd-enrollment"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { EmailWithChip } from "#/components/EmailVerification/EmailAddressesAccountSettings"
import usePartnerAccess, {
  useGetPartnerInviteStatus,
  useUserRole
} from "#/components/partner-access/hooks/usePartnerAccess"
import { InviteStatusResult, PartnerInviteStatus } from "#/components/partner-access/partnerAccessTypes"
import { SettingsPageDivider } from "#/pages/account/page"
import { carrotClient } from "#/utils/CarrotClient"
import { useToggle } from "#/utils/Hooks"
import { Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { FormTextField, useWatch } from "@carrotfertility/carotene-core-x"
import { useQuery } from "@tanstack/react-query"
import { default as React, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { CommunicationSettings } from "../CommunicationSettings"
import { PartnerGenderIdentitySettings, PartnerSexSettings } from "../MemberPartnerPersonalSettings"
import PasswordSettings from "../PasswordSettings"
import { PartnerAccessInformationActive } from "./PartnerAccessInformationActive"
import { PartnerDateOfBirth } from "./PartnerDateOfBirth"
import { PartnerEmail } from "./PartnerEmail"
import { PartnerRadioOptions } from "./PartnerRadioOptions"
import { PartnerHasScheduledServicesAlert } from "./PartnerSupportAlerts"
import { RemovePartnerButton } from "./RemovePartnerButton"
import { EnrollmentType } from "#/components/cmd-enrollment/EnrollmentModal"
import { UpdateInsuranceInfoDialog } from "./RemovePartnerQLEModal"
import { PartnerEligibilityStatus } from "#/utils/PartnerEligibilityStatus"

const usePartnerEligibility = () =>
  useQuery<{ eligibilityStatus: PartnerEligibilityStatus }>({
    queryKey: ["partnerEligibility"],
    queryFn: () => carrotClient.getPartnerEligibility()
  })

function PartnerInformationSettingsBody({
  showPartnerAccess,
  inviteData,
  isPartnerAccessingAccount,
  onPartnerRemoved
}: {
  showPartnerAccess: boolean
  inviteData?: InviteStatusResult
  isPartnerAccessingAccount: boolean
  onPartnerRemoved: () => void
}): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()

  const [showBenefitEnrollmentModal, toggleBenefitEnrollmentModal] = useToggle(false)
  const inviteStatusIsActiveOrInvited = inviteData && inviteData?.status !== PartnerInviteStatus.NONE
  const showPartnerAccessPartnerActiveOrInvited = showPartnerAccess && inviteStatusIsActiveOrInvited

  const partnerFields = useWatch({
    name: [
      "partnerFirstName",
      "partnerLastName",
      "partnerPreferredName",
      "partnerDateOfBirth",
      "partnerSex",
      "partnerGenderIdentity",
      "partnerGenderIdentityOther",
      "partnerEmail"
    ]
  })

  const {
    partnerFirstName,
    partnerLastName,
    partnerPreferredName,
    partnerDateOfBirth,
    partnerSex,
    partnerGenderIdentity,
    partnerGenderIdentityOther,
    partnerEmail
  } = useCurrentUser()
  const showRemovePartnerButton =
    !isPartnerAccessingAccount &&
    (partnerFirstName ||
      partnerLastName ||
      partnerPreferredName ||
      partnerDateOfBirth ||
      partnerSex ||
      partnerGenderIdentity ||
      partnerGenderIdentityOther ||
      partnerEmail)

  const { eligibilityStatus } = usePartnerEligibility().data ?? {}

  const isPartnerOnIdCard = partnerFirstName && eligibilityStatus === PartnerEligibilityStatus.ELIGIBLE

  const hasAnyPartnerInfo = partnerFields.some((field) => field && field !== "nullOut")

  const partnerScheduledServices = useQuery({
    queryKey: ["partnerScheduledServices"],
    queryFn: () => carrotClient.getPartnerScheduledServices(),
    enabled: !!hasAnyPartnerInfo
  })
  const partnerHasScheduledServices = hasAnyPartnerInfo && partnerScheduledServices.data?.length > 0
  const partnerInfoIsReadOnly = hasAnyPartnerInfo && partnerHasScheduledServices

  function validate(fieldValue: string) {
    if (showPartnerAccess) {
      if (inviteStatusIsActiveOrInvited && fieldValue === "") {
        return intl.formatMessage({ defaultMessage: "Required" })
      }
    }
    return hasAnyPartnerInfo && !fieldValue?.trim() ? intl.formatMessage({ defaultMessage: "Required" }) : true
  }

  return (
    <>
      <Stack spacing={theme.spacing(theme.tokens.spacing.lg)} id="partnerInformationSettings">
        <Typography variant="h2">
          <FormattedMessage defaultMessage="Partner information" />
        </Typography>
        {hasAnyPartnerInfo && partnerHasScheduledServices && (
          <PartnerHasScheduledServicesAlert scheduledServices={partnerScheduledServices.data} />
        )}
        <FormTextField
          name="partnerFirstName"
          label={intl.formatMessage({ defaultMessage: "First name" })}
          required={showPartnerAccessPartnerActiveOrInvited || hasAnyPartnerInfo}
          registerOptions={{
            validate,
            setValueAs: (value: string) => (value?.trim() === "" ? null : value)
          }}
          disabled={partnerInfoIsReadOnly}
        />
        <FormTextField
          name="partnerLastName"
          label={intl.formatMessage({ defaultMessage: "Last name" })}
          required={showPartnerAccessPartnerActiveOrInvited || hasAnyPartnerInfo}
          registerOptions={{
            validate,
            setValueAs: (value: string) => (value?.trim() === "" ? null : value)
          }}
          disabled={partnerInfoIsReadOnly}
        />
        <FormTextField
          name="partnerPreferredName"
          label={intl.formatMessage({ defaultMessage: "Preferred name" })}
          inputProps={{ maxLength: 200 }}
        />
        <PartnerDateOfBirth {...{ inviteData, hasAnyPartnerInfo }} disabled={partnerInfoIsReadOnly} />
        <PartnerGenderIdentitySettings
          names={{ genderIdentity: "partnerGenderIdentity", genderIdentityOther: "partnerGenderIdentityOther" }}
        />
        <PartnerSexSettings name="partnerSex" />
        <PartnerEmail {...{ inviteData }} />
        {eligibilityStatus === PartnerEligibilityStatus.POTENTIALLY_ELIGIBLE_MISSING_PARTNER_INFO && (
          <PartnerRadioOptions />
        )}
      </Stack>
      {showBenefitEnrollmentModal && <BenefitEnrollmentFlowContainer onExit={toggleBenefitEnrollmentModal} />}
      {showRemovePartnerButton && (
        <RemovePartnerButton
          disabled={partnerInfoIsReadOnly}
          inviteData={inviteData}
          onPartnerRemoved={onPartnerRemoved}
          isPartnerOnIdCard={isPartnerOnIdCard}
        />
      )}
    </>
  )
}
// When in partner view
function PartnerAccessPartnerViewSettings(): JSX.Element {
  const { partnerEmail } = useCurrentUser()
  const theme = useTheme()
  const intl = useIntl()
  return (
    <>
      <SettingsPageDivider />
      <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
        <Typography variant="h2">
          <FormattedMessage defaultMessage="Partner email address" />
        </Typography>
        <EmailWithChip
          email={partnerEmail}
          chipLabel={intl.formatMessage({
            defaultMessage: "partner sign-in"
          })}
        />
      </Stack>
      <SettingsPageDivider />
      <PasswordSettings isPartnerSection />
      <SettingsPageDivider />
      <CommunicationSettings isForPartner={true} />
    </>
  )
}

export function PartnerInformationSettingsPartnerSupport(): JSX.Element {
  const { showPartnerAccess } = usePartnerAccess()
  const { inviteData } = useGetPartnerInviteStatus()
  const { isPartnerAccessingAccount } = useUserRole()
  const [showInsuranceDialog, setShowInsuranceDialog] = useState(false)
  const [showBenefitEnrollmentModal, toggleBenefitEnrollmentModal] = useToggle(false)
  const {
    isUsa,
    company: { hasACA, carrotLite }
  } = useCurrentUser()
  //dont show if !isUsa or if ACA exempt
  const handlePartnerRemoved = () => {
    if (isUsa && hasACA && !carrotLite) {
      setShowInsuranceDialog(true)
    }
  }

  return (
    <>
      <PartnerInformationSettingsBody
        showPartnerAccess={showPartnerAccess}
        inviteData={inviteData}
        isPartnerAccessingAccount={isPartnerAccessingAccount}
        onPartnerRemoved={handlePartnerRemoved}
      />
      {isPartnerAccessingAccount && <PartnerAccessPartnerViewSettings />}
      {!isPartnerAccessingAccount && <PartnerAccessInformationActive inviteData={inviteData} />}
      {showInsuranceDialog && (
        <UpdateInsuranceInfoDialog
          open={showInsuranceDialog}
          onClose={() => setShowInsuranceDialog(false)}
          onUpdateInsurance={toggleBenefitEnrollmentModal}
        />
      )}
      {showBenefitEnrollmentModal && (
        <BenefitEnrollmentFlowContainer
          enrollmentType={EnrollmentType.QLE_RE_ENROLLMENT}
          onExit={toggleBenefitEnrollmentModal}
        />
      )}
    </>
  )
}
